import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoSection } from '../shared/photo';
import { pendingPhotoSection, rememberPicker } from './photoDraft';

type Options = {
  enabled?: boolean;
  onRecovery?: (section: PhotoSection) => void;
};
// Inputs stay mounted throughout the native app handoff. Recovery is opt-in so
// profile-picture selection cannot restore or overwrite an album contribution.
export function usePhotoPicker(onFile: (file: File, section: PhotoSection) => void, options: Options = {}) {
  const library = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);
  const cameraRecovery = useRef<HTMLInputElement>(null);
  const callback = useRef(onFile);
  const settings = useRef(options);
  const active = useRef<HTMLInputElement | null>(null);
  const received = useRef<File | null>(null);
  const scan = useRef<() => void>(() => {});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [message, setMessage] = useState('');
  useEffect(() => {
    callback.current = onFile;
    settings.current = options;
  }, [onFile, options]);
  useEffect(() => {
    if (options.enabled !== false) scan.current();
  }, [options.enabled]);

  useEffect(() => {
    const inputs = [library.current, camera.current, cameraRecovery.current].filter(
      (input): input is HTMLInputElement => input !== null,
    );
    const sectionOf = (input: HTMLInputElement): PhotoSection =>
      input === library.current ? 'memories' : 'celebration';
    function receive(input: HTMLInputElement) {
      const file = input.files?.[0];
      // On a restored page, access may still be loading. Leave the FileList
      // untouched and consume it when access is known instead of losing it.
      if (!file || file === received.current || settings.current.enabled === false) return false;
      clearTimeout(timer.current);
      active.current = null;
      received.current = file;
      try {
        callback.current(file, sectionOf(input));
        if (settings.current.onRecovery) rememberPicker(null);
        setMessage('');
      } catch {
        received.current = null;
        setMessage('We couldn’t open that photo. Please try again.');
        settings.current.onRecovery?.(sectionOf(input));
      }
      return true;
    }
    function changed(event: Event) {
      receive(event.currentTarget as HTMLInputElement);
    }
    function report(input: HTMLInputElement, text: string) {
      clearTimeout(timer.current);
      active.current = null;
      setMessage(text);
      settings.current.onRecovery?.(sectionOf(input));
    }
    function cancelled(event: Event) {
      const input = event.currentTarget as HTMLInputElement;
      if (receive(input) || input.files?.length) return;
      if (settings.current.onRecovery) rememberPicker(null);
      report(
        input,
        sectionOf(input) === 'celebration'
          ? 'No photo was returned. Try the camera again, or choose the photo if it was saved on your phone.'
          : 'No photo was selected. Choose a saved photo from your library.',
      );
    }
    function resumed() {
      if (document.visibilityState === 'hidden') return;
      // Some Android restores provide a FileList before listeners are attached,
      // or omit the change event. Scan on mount, pageshow and access readiness.
      if (inputs.some(receive) || !active.current) return;
      clearTimeout(timer.current);
      let attempts = 0;
      const check = () => {
        const input = active.current;
        if (!input || document.visibilityState === 'hidden' || receive(input)) return;
        if (++attempts < 40) timer.current = setTimeout(check, 250);
        else
          report(
            input,
            sectionOf(input) === 'celebration'
              ? 'The camera hasn’t returned a photo. Try again, or choose the photo if it was saved on your phone.'
              : 'The photo picker hasn’t returned a photo. Please choose it again.',
          );
      };
      check();
    }
    scan.current = resumed;
    inputs.forEach((input) => {
      input.addEventListener('input', changed);
      input.addEventListener('change', changed);
      input.addEventListener('cancel', cancelled);
    });
    window.addEventListener('focus', resumed);
    window.addEventListener('pageshow', resumed);
    document.addEventListener('visibilitychange', resumed);
    const pending = settings.current.onRecovery && pendingPhotoSection();
    if (pending) {
      active.current = pending === 'celebration' ? camera.current : library.current;
      setMessage(
        'Your browser restarted before the photo reached the review screen. If the photo was saved on your phone, choose it below.',
      );
      settings.current.onRecovery?.(pending);
    }
    resumed();
    return () => {
      clearTimeout(timer.current);
      scan.current = () => {};
      inputs.forEach((input) => {
        input.removeEventListener('input', changed);
        input.removeEventListener('change', changed);
        input.removeEventListener('cancel', cancelled);
      });
      window.removeEventListener('focus', resumed);
      window.removeEventListener('pageshow', resumed);
      document.removeEventListener('visibilitychange', resumed);
    };
  }, []);

  const reset = useCallback(() => {
    clearTimeout(timer.current);
    active.current = null;
    received.current = null;
    for (const input of [library.current, camera.current, cameraRecovery.current])
      if (input) input.value = '';
    if (settings.current.onRecovery) rememberPicker(null);
    setMessage('');
  }, []);
  function open(input: HTMLInputElement | null) {
    if (!input || settings.current.enabled === false) return;
    clearTimeout(timer.current);
    // Only clear on an explicit new selection, never when returning to the page.
    for (const old of [library.current, camera.current, cameraRecovery.current]) if (old) old.value = '';
    received.current = null;
    active.current = input;
    if (settings.current.onRecovery) rememberPicker(input === library.current ? 'memories' : 'celebration');
    setMessage('Waiting for your photo. If the camera returns without it, try again or take a photo in your Camera app, then choose the saved picture.');
    // Leave recovery UI behind the native picker even if Android emits no
    // change, cancel, focus or visibility event when returning.
    settings.current.onRecovery?.(input === library.current ? 'memories' : 'celebration');
    try { input.click(); }
    catch { setMessage('The photo picker couldn’t open. Please try again or choose a saved photo.'); }
  }
  return {
    reset,
    library,
    camera,
    cameraRecovery,
    message,
    openLibrary: () => open(library.current),
    openCamera: () => open(camera.current),
    recoverCamera: () => open(cameraRecovery.current),
  };
}
